/**
 * React Query hooks for Care Teams
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  careTeamsApi,
  type CareTeam,
  type CareTeamCreate,
  type CareTeamUpdate,
} from '../api/care-teams';

/**
 * Hook to fetch all care teams
 */
export function useCareTeams(params?: {
  hospital_id?: number;
  is_active?: boolean;
  search?: string;
  skip?: number;
  limit?: number;
}) {
  return useQuery({
    queryKey: ['care-teams', params],
    queryFn: () => careTeamsApi.getAll(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to fetch a single care team by ID
 */
export function useCareTeam(careTeamId: number | null) {
  return useQuery({
    queryKey: ['care-teams', careTeamId],
    queryFn: () => careTeamsApi.getById(careTeamId!),
    enabled: !!careTeamId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to create a new care team
 */
export function useCreateCareTeam() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CareTeamCreate) => careTeamsApi.create(data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['care-teams'] });
      queryClient.invalidateQueries({ queryKey: ['coordinator-dashboard'] });
      toast.success('Care Team Created', {
        description: response.message || 'Care team created successfully',
      });
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.detail || 'Failed to create care team';
      toast.error('Creation Failed', {
        description: errorMessage,
      });
    },
  });
}

/**
 * Hook to update a care team
 */
export function useUpdateCareTeam() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ careTeamId, data }: { careTeamId: number; data: CareTeamUpdate }) =>
      careTeamsApi.update(careTeamId, data),
    onSuccess: (response, variables) => {
      queryClient.invalidateQueries({ queryKey: ['care-teams'] });
      queryClient.invalidateQueries({ queryKey: ['care-teams', variables.careTeamId] });
      queryClient.invalidateQueries({ queryKey: ['coordinator-dashboard'] });
      toast.success('Care Team Updated', {
        description: response.message || 'Care team updated successfully',
      });
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.detail || 'Failed to update care team';
      toast.error('Update Failed', {
        description: errorMessage,
      });
    },
  });
}

/**
 * Hook to delete a care team
 */
export function useDeleteCareTeam() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (careTeamId: number) => careTeamsApi.delete(careTeamId),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['care-teams'] });
      queryClient.invalidateQueries({ queryKey: ['coordinator-dashboard'] });
      toast.success('Care Team Deleted', {
        description: response.message || 'Care team deleted successfully',
      });
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.detail || 'Failed to delete care team';
      toast.error('Deletion Failed', {
        description: errorMessage,
      });
    },
  });
}